import React, { useRef, useEffect } from 'react';
import { MessageSquare, ChevronDown } from 'lucide-react';

const ChatInterface = ({ 
  chatHistory, 
  chatQuery, 
  setChatQuery, 
  handleChatSubmit, 
  isTyping, 
  autoScroll, 
  scrollToBottom,
  handleActionClick 
}) => {
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Scroll to bottom of chat when new messages are added, but only if autoScroll is enabled
  useEffect(() => {
    if (chatEndRef.current && autoScroll) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chatHistory, autoScroll]);

  return (
    <div className="border border-gray-300 rounded-lg mb-4 overflow-hidden">
      <div className="bg-gray-50 p-3 border-b border-gray-300">
        <h3 className="font-medium text-gray-800 flex items-center">
          <MessageSquare size={18} className="mr-2 text-primaryDark" />
          Chat Query
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Ask natural language questions to find specific client segments
        </p>
      </div>

      <div
        ref={chatContainerRef}
        className="bg-white p-4 h-64 overflow-y-auto"
        style={{ overscrollBehavior: 'contain' }}
      >
        {chatHistory.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageSquare size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Ask a question to find specific client segments</p>
            <p className="text-xs mt-2">Example: "Show me tech entrepreneurs in California" or "Find clients interested in philanthropy"</p>
          </div>
        ) : (
          <div className="space-y-4">
            {chatHistory.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3/4 rounded-lg px-4 py-2 ${message.role === 'user'
                    ? 'bg-blue-100 text-primaryDark'
                    : 'bg-gray-100 text-gray-800'
                    }`}
                >
                  <p className="text-sm">{message.content}</p>
                  {message.tableData && message.tableData.length > 0 && (() => {
                    const ID_KEYS = /^(id|.*_id|.*id)$/i;
                    const visibleKeys = Object.keys(message.tableData[0]).filter(k => !ID_KEYS.test(k));
                    return (
                      <div className="mt-2 overflow-x-auto max-w-full">
                        <table className="text-xs border-collapse w-full">
                          <thead>
                            <tr>
                              {visibleKeys.map(key => (
                                <th
                                  key={key}
                                  className="border border-gray-300 px-2 py-1 bg-gray-200 text-left font-semibold whitespace-nowrap"
                                >
                                  {key.replace(/_/g, ' ')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {message.tableData.map((row, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                {visibleKeys.map(key => (
                                  <td key={key} className="border border-gray-300 px-2 py-1 whitespace-nowrap">
                                    {row[key] ?? ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                  {message.isTyping && (
                    <span className="inline-block ml-1">
                      <span className="typing-dot animate-pulse">.</span>
                      <span className="typing-dot animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
                      <span className="typing-dot animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
                    </span>
                  )}
                  {message.action && (
                    <button
                      onClick={() => handleActionClick(message.action)}
                      className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                    >
                      Apply These Filters
                    </button>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 max-w-3/4 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '0s' }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      <div className="border-t border-gray-300 bg-white">
        {!autoScroll && chatHistory.length > 2 && (
          <div className="px-3 py-1 text-center">
            <button
              onClick={scrollToBottom}
              className="text-xs text-primaryDark hover:text-primaryDark flex items-center mx-auto"
            >
              <ChevronDown size={14} className="mr-1" />
              Scroll to bottom
            </button>
          </div>
        )}

        <form onSubmit={handleChatSubmit} className="p-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={chatQuery}
              onChange={(e) => setChatQuery(e.target.value)}
              placeholder="Ask a question about client segments..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              disabled={isTyping}
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
              disabled={isTyping || !chatQuery.trim()}
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;